import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getReinforcementsList, getReinforcementsMinimal, deleteReinforcement, setReinforcementsPage, clearCurrentReinforcement } from '../../actions/reinforcementActions';
import { getTags } from '../../actions/tagActions';
import ContentManagementSidebar from '../content-management/ContentManagementSidebar';
import TableView from '../content-management/views/TableView';
import MobileView from '../content-management/views/MobileView';
import TableSkeleton from '../content-management/views/TableView/TableSkeleton';
import MobileSkeleton from '../content-management/views/MobileView/MobileSkeleton';
import DeleteConfirmationModal from '../content-management/common/DeleteConfirmationModal';
import PaginationControls from '../common/PaginationControls';
import MultiSelectSearchFilter from '../content-management/common/MultiSelectSearchFilter';
import { Plus, Filter, X } from 'lucide-react';
import { loadContentManagementFilters, saveContentManagementFilters } from '../../utils/contentManagementFilters';
import { isAdminRole } from '../../utils/roleHelpers';

const ReinforcementsManagementContainer = ({
  auth: { user },
  reinforcement: { reinforcements, minimalReinforcements, loading, currentPage, totalPages, totalReinforcements, hasNextPage, hasPrevPage },
  tag: { tags },
  getReinforcementsList,
  deleteReinforcement,
  getTags,
  setReinforcementsPage,
  clearCurrentReinforcement,
  getReinforcementsMinimal,
  hideSidebar
}) => {
  const navigate = useNavigate();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTitles, setSelectedTitles] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [filtersHydrated, setFiltersHydrated] = useState(false);
  const filtersLoadedRef = useRef(false);

  // Role detection
  const isAdmin = isAdminRole(user?.role);
  const isInstructor = user?.role === 'instructor';

  // Instructors have read-only access to reinforcements
  const isReadOnly = isInstructor;

  useEffect(() => {
    getTags();
    getReinforcementsMinimal();
  }, [getTags, getReinforcementsMinimal]);

  useEffect(() => {
    const saved = loadContentManagementFilters('Reinforcements');
    if (saved?.filters) {
      setSelectedTitles(saved.filters.selectedTitles || []);
      setSelectedTags(saved.filters.selectedTags || []);
      if (saved.count > 0) {
        setShowFilters(true);
      }
    }
    filtersLoadedRef.current = true;
    setFiltersHydrated(true);
  }, []);

  useEffect(() => {
    if (!filtersHydrated) return;
    const filters = {};
    if (selectedTitles.length > 0) {
      filters.title = selectedTitles;
    }
    if (selectedTags.length > 0) {
      filters.tags = selectedTags;
    }
    getReinforcementsList(currentPage, 10, filters);
  }, [filtersHydrated, currentPage, selectedTitles, selectedTags, getReinforcementsList]);

  useEffect(() => {
    if (!filtersLoadedRef.current) return;
    const count = selectedTitles.length + selectedTags.length;
    saveContentManagementFilters('Reinforcements', { selectedTitles, selectedTags }, count);
  }, [selectedTitles, selectedTags]);

  const handleAdd = () => {
    if (isReadOnly) return; // Prevent add in read-only mode
    // Clear current reinforcement before navigating to prevent form pre-population
    clearCurrentReinforcement();
    navigate('/content-management/reinforcements/new');
  };

  const handleView = (item) => {
    navigate(`/content-management/reinforcements/${item._id}/view`);
  };

  const handleEdit = (item) => {
    if (isReadOnly) return; // Prevent edit in read-only mode
    navigate(`/content-management/reinforcements/${item._id}/edit`);
  };

  const handleDeleteClick = (item) => {
    if (isReadOnly) return; // Prevent delete in read-only mode
    setItemToDelete(item);
    setShowDeleteConfirmation(true);
  };

  const handleDeleteConfirm = () => {
    deleteReinforcement(itemToDelete._id);
    setShowDeleteConfirmation(false);
    setItemToDelete(null);
  };

  const handleDeleteCancel = () => {
    setShowDeleteConfirmation(false);
    setItemToDelete(null);
  };

  const handleTabChange = (tabId) => {
    if (tabId === 'Reinforcements') {
      // Already on reinforcements page, do nothing
      return;
    }
    // Navigate to content management with selected tab
    navigate('/content-management', { state: { activeTab: tabId } });
  };

  const getItemTitle = (item) => {
    return item.title;
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

    const getThumbnailUrl = (thumbnail) => {
    if (!thumbnail) return null;
    if (thumbnail.url) return thumbnail.url;
    if (thumbnail.data && thumbnail.contentType) return `data:${thumbnail.contentType};base64,${thumbnail.data}`;
    return null;
  };

  // Handle pagination - only update Redux state, useEffect will fetch data
  const handlePageChange = (newPage) => {
    setReinforcementsPage(newPage);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const clearAllFilters = () => {
    setSelectedTitles([]);
    setSelectedTags([]);
    setReinforcementsPage(1);
  };

  const hasActiveFilters = selectedTitles.length > 0 || selectedTags.length > 0;

  // Get unique titles from reinforcements
  const uniqueTitles = minimalReinforcements && minimalReinforcements.length > 0
    ? [...new Set(minimalReinforcements.map(r => r.title))].filter(Boolean)
    : [];

  const content = (
    <div className={hideSidebar ? '' : "flex-1 border-t md:border-t-0 md:border-l border-gray-200 p-6 min-w-0 overflow-x-auto"}>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold text-[#054581]">
              Manage Reinforcements
            </h2>
                {isReadOnly && (
                  <span className="px-3 py-1 bg-orange-100 text-orange-800 text-sm font-medium rounded-full">
                    Read Only
                  </span>
                )}
                {totalReinforcements > 0 && (
                  <span className="text-sm text-gray-500">
                    ({totalReinforcements} total)
                  </span>
                )}
              </div>
              <div className="flex items-center gap-3">
                <button
                  onClick={toggleFilters}
                  className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                    hasActiveFilters
                      ? 'bg-[#1086a4] text-white shadow-md'
                      : 'bg-white border border-[#1086a4] text-[#054581] hover:bg-[#f9fafb]'
                  }`}
                >
                  <Filter size={18} />
                  <span>{showFilters ? 'Hide Filters' : 'Show Filters'}</span>
                  {hasActiveFilters && (
                    <span className="bg-white text-[#1086a4] text-xs px-2 py-0.5 rounded-full font-semibold">
                      Active
                    </span>
                  )}
                </button>
                {!isReadOnly && (
                  <button
                    onClick={handleAdd}
                    className="bg-[#1086a4] text-white px-2 sm:px-4 py-2 rounded-lg font-semibold shadow hover:bg-[#054581] transition-colors flex items-center gap-1 sm:gap-2 text-sm sm:text-base"
                  >
                    <Plus className="w-4 h-4 flex-shrink-0" />
                    <span>Add</span>
                  </button>
                )}
              </div>
            </div>

            {/* Filter UI */}
            {showFilters && (
              <div className="mb-6 space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-semibold text-[#054581]">Filter Reinforcements</h3>
                  <button
                    onClick={clearAllFilters}
                    disabled={!hasActiveFilters}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                      hasActiveFilters
                        ? 'bg-red-500 hover:bg-red-600 text-white cursor-pointer'
                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                  >
                    <X className="w-3 h-3" />
                    Clear All Filters
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <MultiSelectSearchFilter
                    items={uniqueTitles.map(title => ({ _id: title, title: title }))}
                    selectedItems={selectedTitles}
                    onSelectionChange={(selected) => {
                      setSelectedTitles(selected);
                      setReinforcementsPage(1);
                    }}
                    title="Titles"
                    placeholder="Search titles..."
                    emptyMessage="No titles available"
                  />

                  <MultiSelectSearchFilter
                    items={tags}
                    selectedItems={selectedTags}
                    onSelectionChange={(selected) => {
                      setSelectedTags(selected);
                      setReinforcementsPage(1);
                    }}
                    title="Tags"
                    placeholder="Search tags..."
                    emptyMessage="No tags available"
                  />
                </div>
              </div>
            )}

            {loading ? (
              <>
                {/* Desktop Skeleton */}
                <div className="hidden md:block">
                  <TableSkeleton rowCount={10} activeTab="Reinforcements" />
                </div>

                {/* Mobile Skeleton */}
                <div className="md:hidden">
                  <MobileSkeleton cardCount={10} />
                </div>
              </>
            ) : (
              <>
                {/* Mobile View */}
                <div className="md:hidden">
                  <MobileView
                    items={reinforcements}
                    activeTab="Reinforcements"
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDeleteClick}
                    getThumbnailUrl={getThumbnailUrl}
                    truncateText={truncateText}
                    getItemTitle={getItemTitle}
                    isReadOnly={isReadOnly}
                  />
                </div>

                {/* Desktop View */}
                <div className="hidden md:block">
                  <TableView
                    items={reinforcements}
                    activeTab="Reinforcements"
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDeleteClick}
                    getThumbnailUrl={getThumbnailUrl}
                    truncateText={truncateText}
                    getItemTitle={getItemTitle}
                    isReadOnly={isReadOnly}
                  />
                </div>

                {/* Pagination Controls */}
                <PaginationControls
                  currentPage={currentPage}
                  totalPages={totalPages}
                  totalItems={totalReinforcements}
                  itemsPerPage={10}
                  onPageChange={handlePageChange}
                  hasNextPage={hasNextPage}
                  hasPrevPage={hasPrevPage}
                  itemName="reinforcements"
                />
              </>
            )}

            {/* Delete Confirmation Modal */}
            <DeleteConfirmationModal
              isOpen={showDeleteConfirmation}
              onClose={handleDeleteCancel}
              onConfirm={handleDeleteConfirm}
              itemTitle={itemToDelete ? getItemTitle(itemToDelete) : ''}
              itemType="Reinforcement"
            />
      </div>
    </div>
  );

  if (hideSidebar) {
    return <>{content}</>;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-4 px-2 sm:py-8 sm:px-4 pb-20 md:pb-4">
      <div className="mx-auto bg-white rounded-lg shadow-sm flex flex-col md:flex-row max-w-full overflow-hidden">
        <ContentManagementSidebar
          activeTab="Reinforcements"
          onTabChange={handleTabChange}
          isAdmin={isAdmin}
          isInstructor={isInstructor}
        />
        {content}
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  auth: state.auth,
  reinforcement: state.reinforcement,
  tag: state.tag
});

export default connect(
  mapStateToProps,
  {
    getReinforcementsList,
    getReinforcementsMinimal,
    deleteReinforcement,
    getTags,
    setReinforcementsPage,
    clearCurrentReinforcement
  }
)(ReinforcementsManagementContainer);
